import React, { useContext, useState } from 'react';
import { Alert, Button } from '@mui/material';
import ReactJson from 'react18-json-view';
import axios from '../../../_utils/axios';
import Modal from '../Modal/Modal';
import PolarisIcon from '../PolarisIcon';
import { InventoryFileEntity, isActiveVersionAVersion } from '@/redux/slices/slice_types';
import { selectActiveVersion } from '@/redux/selectors/titleSelectors';
import { selectActiveVersion as selectActiveVersionV2 } from '@/redux/selectors/titleV2Selectors';
import styled from '@emotion/styled';
import { useAppSelector } from '@/redux/hooks';
import { insertWordBreaks } from '@/_utils/utils';
import OverflowMenuItem from '../OverflowMenu/OverflowMenuItem';
import { ConfigContext } from '@/contextAPI/configContext';

type OPInventoryFileMediaInfoProps = {
  file: InventoryFileEntity;
};

const InventoryFileMediaInfo = ({ file }: OPInventoryFileMediaInfoProps) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mediaInfo, setMediaInfo] = useState(null);
  const [isRequested, setIsRequested] = useState(false);
  const activeVersion = useAppSelector(isTitleV2 ? selectActiveVersionV2 : selectActiveVersion);
  if (!activeVersion) return null;

  const getMediaInfo = () => {
    // This check is needed to make sure we can read `activeVersion`'s properties
    if (!isActiveVersionAVersion(activeVersion)) {
      throw new Error('`activeVersion` is not a version');
    }

    setIsRequested(false);
    setLoading(true);
    setOpen(true);
    axios
      .get(`inventory/titles/${activeVersion.id}/files/${file.id}/media_info`)
      .then((res) => {
        const response = res.data?.response;
        if (!response) return null;
        else if (response?.message?.toLowerCase() === 'has been requested') {
          setIsRequested(true);
          setLoading(false);
        } else setMediaInfo(response?.body);
      })
      .catch((err) => console.error(err.response))
      .then(() => setLoading(false));
  };

  const renderModalContent = () => {
    if (loading) {
      return (
        <CenteredDiv>
          <Alert severity="info">Loading media info...</Alert>
        </CenteredDiv>
      );
    } else if (isRequested) {
      return (
        <CenteredDiv>
          <span style={{ marginBottom: 20 }}>
            <Alert severity="success">Media info has been requested.</Alert>
          </span>
          <Button color="primary" variant="outlined" onClick={getMediaInfo}>
            Refresh
          </Button>
        </CenteredDiv>
      );
    } else if (mediaInfo) {
      return <ReactJson collapseStringsAfterLength={50} enableClipboard={false} src={mediaInfo} style={{ padding: 12 }} theme="winter-is-coming" />;
    } else {
      return (
        <CenteredDiv>
          <Alert severity="error">There was an error retrieving media info.</Alert>
        </CenteredDiv>
      );
    }
  };

  const handleOnClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    getMediaInfo();
  };

  return (
    <>
      <OverflowMenuItem onClick={handleOnClick}>
        <PolarisIcon icon="info" height={15} />
        Media info
      </OverflowMenuItem>
      {open && (
        <StyledModal
          loading={loading}
          open={open}
          onClose={() => setOpen(false)}
          title={<>Media Info: {insertWordBreaks(file.name)}</>}
          icon={<PolarisIcon icon="info" />}
          addMinHeight={isRequested}
        >
          {renderModalContent()}
        </StyledModal>
      )}
    </>
  );
};

export default InventoryFileMediaInfo;

const CenteredDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;

  & > span {
    display: flex;
    align-items: center;
  }
`;

const StyledModal = styled(Modal)<{ addMinHeight: boolean }>`
  ${({ addMinHeight }) =>
    addMinHeight &&
    `
  .MuiPaper-root {
    min-height: 290px;
  }
  `}
`;
